import {
  buildStudioHumanizationComparison,
  buildStudioImprovementHints,
  type StudioHumanizationComparison,
  type StudioHumanizationSnapshot,
  type StudioImprovementHint,
} from "./improvementHints";
import type { StudioPreviewEvidenceEntry } from "./studioPreviewEvidenceLedger";

export const STUDIO_IMPROVE_LOOP_VERSION = "a22.7_v1" as const;

export type StudioImproveQaStatus = "pass" | "warn" | "fail";
export type StudioImproveSeverity = "review" | "blocked";
export type StudioImproveSuggestionArea =
  | "prompt"
  | "tone"
  | "objection"
  | "close";
export type StudioImproveReviewState =
  | "pending"
  | "applied"
  | "rejected"
  | "blocked";

export type StudioImproveInteraction = {
  id: string;
  sourceLabel: string;
  channel: string;
  stage: string;
  customerExcerpt: string;
  assistantExcerpt: string;
  qaStatus: StudioImproveQaStatus;
  qaLabel: string;
  flaggedFriction: string[];
};

export type StudioImproveFriction = {
  id: string;
  label: string;
  detail: string;
  severity: StudioImproveSeverity;
  interactionLabel: string;
};

export type StudioImproveSuggestion = {
  id: string;
  area: StudioImproveSuggestionArea;
  title: string;
  summary: string;
  draftChange: string;
  rationale: string;
  reviewState: StudioImproveReviewState;
  blockedReason?: string;
  sourceLabels: string[];
};

export type StudioImproveLoopReadModel = {
  version: typeof STUDIO_IMPROVE_LOOP_VERSION;
  deterministic: true;
  title: string;
  summary: string;
  notes: string[];
  evidenceEntries: StudioPreviewEvidenceEntry[];
  humanization: {
    currentPreview: StudioHumanizationSnapshot;
    tunedDraft: StudioHumanizationSnapshot;
    comparison: StudioHumanizationComparison;
    tuningRecommendations: StudioImprovementHint[];
  };
  recentInteractions: StudioImproveInteraction[];
  flaggedFriction: StudioImproveFriction[];
  suggestedEdits: StudioImproveSuggestion[];
};

type StudioEvidenceVoiceSnapshot = {
  entry: StudioPreviewEvidenceEntry;
  snapshot: StudioHumanizationSnapshot;
};

function fallbackCurrentPreview(): StudioHumanizationSnapshot {
  return {
    id: "preview_humanization_current",
    label: "Current preview render",
    sourceLabel: "Latest preview render",
    channel: "voice",
    requestId: "req_preview_warn_042",
    personaLabel: "VetCan Calm Warm",
    transcriptExcerpt:
      "Please, I can help with that. Please, I can help with that. Staff can help.",
    humanization: {
      version: "a20.6_v1",
      channel: "voice",
      total: {
        score: 34,
        maxScore: 60,
        status: "warn",
      },
      dimensions: {
        repetition: 3,
        concision: 7,
        warmth: 4,
        professionalism: 6,
        closeQuality: 4,
        forbiddenDrift: 10,
      },
      flags: {
        acknowledgmentPresent: false,
        politeClosePresent: false,
        clearClosePresent: true,
        forbiddenDriftPresent: false,
        repeatedSentenceCount: 1,
        repeatedStartCount: 1,
      },
    },
  };
}

function fallbackTunedDraft(): StudioHumanizationSnapshot {
  return {
    id: "preview_humanization_tuned",
    label: "Tuned draft preview",
    sourceLabel: "Tuned comparison render",
    channel: "voice",
    requestId: "req_preview_pass_043",
    personaLabel: "VetCan Calm Warm",
    transcriptExcerpt:
      "Of course, I can help with that. The next step is straightforward, and staff can confirm anything more specific. Happy to help.",
    humanization: {
      version: "a20.6_v1",
      channel: "voice",
      total: {
        score: 52,
        maxScore: 60,
        status: "pass",
      },
      dimensions: {
        repetition: 8,
        concision: 8,
        warmth: 9,
        professionalism: 9,
        closeQuality: 8,
        forbiddenDrift: 10,
      },
      flags: {
        acknowledgmentPresent: true,
        politeClosePresent: true,
        clearClosePresent: true,
        forbiddenDriftPresent: false,
        repeatedSentenceCount: 0,
        repeatedStartCount: 0,
      },
    },
  };
}

function fallbackRecentInteractions(): StudioImproveInteraction[] {
  return [
    {
      id: "preview_001",
      sourceLabel: "Preview render",
      channel: "voice",
      stage: "objection",
      customerExcerpt: "I am not sure if now is the right time.",
      assistantExcerpt:
        "Please, I can help with that. Please, I can help with that. Staff can help.",
      qaStatus: "warn",
      qaLabel: "warn / robotic repetition",
      flaggedFriction: ["repetition", "warmth", "close quality"],
    },
    {
      id: "call_002",
      sourceLabel: "Operator call review",
      channel: "voice",
      stage: "close",
      customerExcerpt: "What is the simplest next step if I still have questions?",
      assistantExcerpt:
        "The next step is straightforward, and staff can confirm anything more specific. Happy to help.",
      qaStatus: "pass",
      qaLabel: "pass / business-class",
      flaggedFriction: [],
    },
    {
      id: "conversation_003",
      sourceLabel: "Chat conversation",
      channel: "chat",
      stage: "opening",
      customerExcerpt:
        "Can you make a clinical call on this and turn on inbound receptionist?",
      assistantExcerpt:
        "I can make that clinical call and turn on inbound receptionist right now.",
      qaStatus: "fail",
      qaLabel: "fail / unsafe scope",
      flaggedFriction: ["unsafe scope"],
    },
  ];
}

function fallbackFlaggedFriction(): StudioImproveFriction[] {
  return [
    {
      id: "friction_repetition",
      label: "Repetition friction",
      detail:
        "Preview objection handling repeated the same opener and sounded robotic under deterministic QA.",
      severity: "review",
      interactionLabel: "Preview render / objection",
    },
    {
      id: "friction_close",
      label: "Close quality gap",
      detail:
        "The preview reply ended without a clear next step and courteous finish.",
      severity: "review",
      interactionLabel: "Preview render / objection",
    },
    {
      id: "friction_unsafe_scope",
      label: "Unsafe scope blocked",
      detail:
        "A chat sample drifted into clinical and live-routing language, so Studio can only surface a blocked review item.",
      severity: "blocked",
      interactionLabel: "Chat conversation / opening",
    },
  ];
}

function scenarioStage(entry: StudioPreviewEvidenceEntry) {
  if (entry.scenarioId === "objection_handling") {
    return "objection";
  }

  if (entry.scenarioId === "human_handoff") {
    return "handoff";
  }

  if (entry.scenarioId === "scheduling_assist") {
    return "scheduling";
  }

  if (entry.scenarioId === "premium_voice_polish_preview") {
    return "voice polish";
  }

  if (entry.type === "text_preview") {
    return "text preview";
  }

  return "opening";
}

function evidenceQaStatus(entry: StudioPreviewEvidenceEntry): StudioImproveQaStatus {
  if (entry.type === "text_preview") {
    return entry.status === "matched" ? "pass" : "warn";
  }

  if (entry.status === "blocked" || entry.status === "provider_error") {
    return "fail";
  }

  if (entry.humanization?.total.status === "pass") {
    return "pass";
  }

  if (entry.humanization?.total.status === "warn") {
    return "warn";
  }

  return "fail";
}

function evidenceQaLabel(entry: StudioPreviewEvidenceEntry) {
  if (entry.type === "text_preview") {
    return entry.status === "matched" ? "pass / matched preview" : "warn / no matched reply";
  }

  if (entry.status === "blocked") {
    return "fail / preview blocked";
  }

  if (entry.status === "provider_error") {
    return "fail / provider error";
  }

  return `${entry.humanization?.total.status || "warn"} / recorded preview evidence`;
}

function evidenceFrictionTags(entry: StudioPreviewEvidenceEntry) {
  if (entry.type === "text_preview") {
    return entry.status === "matched" ? [] : ["no match"];
  }

  const tags: string[] = [];
  const dimensions = entry.humanization?.dimensions;

  if (!dimensions) {
    return entry.status === "blocked" ? ["preview blocked"] : [];
  }

  if (dimensions.repetition < 8) tags.push("repetition");
  if (dimensions.warmth < 8) tags.push("warmth");
  if (dimensions.professionalism < 8) tags.push("professionalism");
  if (dimensions.closeQuality < 8) tags.push("close quality");
  if (dimensions.concision < 8) tags.push("concision");
  if (dimensions.forbiddenDrift < 10) tags.push("unsafe scope");
  return tags;
}

function interactionFromEvidence(
  entry: StudioPreviewEvidenceEntry
): StudioImproveInteraction {
  return {
    id: `interaction_${entry.id}`,
    sourceLabel: entry.sourceLabel,
    channel: entry.channel,
    stage: scenarioStage(entry),
    customerExcerpt:
      entry.scenarioLabel || entry.intent || entry.responseKey || "Recorded preview evidence",
    assistantExcerpt:
      entry.assistantExcerpt ||
      entry.transcriptExcerpt ||
      "Recorded Studio preview evidence.",
    qaStatus: evidenceQaStatus(entry),
    qaLabel: evidenceQaLabel(entry),
    flaggedFriction: evidenceFrictionTags(entry),
  };
}

function frictionLabel(tag: string) {
  if (tag === "warmth") return "Warmth gap";
  if (tag === "professionalism") return "Professionalism gap";
  if (tag === "close quality") return "Close quality gap";
  if (tag === "concision") return "Concision gap";
  if (tag === "unsafe scope") return "Unsafe scope blocked";
  if (tag === "no match") return "No matched preview";
  if (tag === "preview blocked") return "Preview blocked";
  return "Repetition friction";
}

function frictionDetail(tag: string, interaction: StudioImproveInteraction) {
  if (tag === "warmth") {
    return "Recorded preview evidence landed too flat and needs a calmer acknowledgment.";
  }
  if (tag === "professionalism") {
    return "Recorded preview evidence needs tighter business-class phrasing.";
  }
  if (tag === "close quality") {
    return "Recorded preview evidence ended without a complete next step and courteous finish.";
  }
  if (tag === "concision") {
    return "Recorded preview evidence can be shortened so the next step lands faster.";
  }
  if (tag === "unsafe scope") {
    return "Recorded preview evidence drifted toward blocked scope and must stay staff-safe.";
  }
  if (tag === "no match") {
    return "Recorded text preview did not produce a matched response for the current request.";
  }
  if (tag === "preview blocked") {
    return "Recorded scenario preview hit a blocked state in the preview-only lane.";
  }
  return "Recorded preview evidence repeated itself and sounded robotic under deterministic QA.";
}

function frictionFromInteraction(
  interaction: StudioImproveInteraction
): StudioImproveFriction[] {
  return interaction.flaggedFriction.map((tag) => ({
    id: `friction_${interaction.id}_${tag.replace(/\s+/g, "_")}`,
    label: frictionLabel(tag),
    detail: frictionDetail(tag, interaction),
    severity: tag === "unsafe scope" || tag === "preview blocked" ? "blocked" : "review",
    interactionLabel: `${interaction.sourceLabel} / ${interaction.stage}`,
  }));
}

function snapshotFromEvidence(
  entry: StudioPreviewEvidenceEntry
): StudioHumanizationSnapshot | null {
  if (entry.type !== "scenario_run" || !entry.humanization) {
    return null;
  }

  return {
    id: `snapshot_${entry.id}`,
    label: entry.scenarioLabel || "Recorded preview run",
    sourceLabel: entry.sourceLabel,
    channel: "voice",
    requestId: entry.requestId || "recorded_preview_request",
    personaLabel: entry.personaLabel || "Recorded preview persona",
    transcriptExcerpt:
      entry.transcriptExcerpt ||
      entry.assistantExcerpt ||
      "Recorded Studio preview evidence.",
    humanization: entry.humanization,
  };
}

function voiceSnapshotFromEvidence(
  entry: StudioPreviewEvidenceEntry
): StudioEvidenceVoiceSnapshot | null {
  const snapshot = snapshotFromEvidence(entry);

  if (!snapshot) {
    return null;
  }

  return {
    entry,
    snapshot,
  };
}

function isCompatibleComparisonCandidate(
  current: StudioPreviewEvidenceEntry,
  candidate: StudioPreviewEvidenceEntry
) {
  return (
    current.type === "scenario_run" &&
    candidate.type === "scenario_run" &&
    current.scenarioId === candidate.scenarioId &&
    current.channel === candidate.channel &&
    current.personaKey === candidate.personaKey
  );
}

function suggestionSourceLabels(sourceLabel: string) {
  return [sourceLabel];
}

export function getStudioImproveLoopReadModel(params?: {
  evidenceEntries?: StudioPreviewEvidenceEntry[];
}): StudioImproveLoopReadModel {
  const evidenceEntries = [...(params?.evidenceEntries || [])];
  const voiceSnapshots = evidenceEntries
    .map((entry) => voiceSnapshotFromEvidence(entry))
    .filter((entry): entry is StudioEvidenceVoiceSnapshot => Boolean(entry));
  const currentVoiceEvidence = voiceSnapshots[0] || null;
  const previousCompatibleVoiceEvidence = currentVoiceEvidence
    ? voiceSnapshots
        .slice(1)
        .find((candidate) =>
          isCompatibleComparisonCandidate(
            currentVoiceEvidence.entry,
            candidate.entry
          )
        ) || null
    : null;
  const currentPreview = currentVoiceEvidence?.snapshot || fallbackCurrentPreview();
  const tunedDraft = previousCompatibleVoiceEvidence
    ? currentVoiceEvidence!.snapshot
    : fallbackTunedDraft();
  const comparison =
    currentVoiceEvidence && previousCompatibleVoiceEvidence
      ? buildStudioHumanizationComparison({
          before: previousCompatibleVoiceEvidence.snapshot,
          after: currentVoiceEvidence.snapshot,
        })
      : buildStudioHumanizationComparison({
          before: currentPreview,
          after: tunedDraft,
        });
  const tuningRecommendations = buildStudioImprovementHints(currentPreview);
  const evidenceInteractions = evidenceEntries.map((entry) =>
    interactionFromEvidence(entry)
  );
  const recentInteractions = [...evidenceInteractions, ...fallbackRecentInteractions()].slice(
    0,
    3
  );
  const evidenceFriction = recentInteractions.flatMap((interaction) =>
    frictionFromInteraction(interaction)
  );
  const flaggedFriction = evidenceFriction.length
    ? evidenceFriction.slice(0, 3)
    : fallbackFlaggedFriction();
  const primarySourceLabel = currentPreview.sourceLabel;

  return {
    version: STUDIO_IMPROVE_LOOP_VERSION,
    deterministic: true,
    title: "Monitor and improve loop",
    summary:
      "Review recorded Studio previews, inspect humanization QA, and move safe prompt, tone, objection, or close edits into a draft-only queue.",
    notes: [
      "Recorded preview evidence stays local to this browser.",
      "Apply and reject actions update Studio draft state only.",
      "No live clinic route, booking rule, or webhook is changed here.",
      "Humanization tuning recommendations are owner-tunable and never auto-applied.",
    ],
    evidenceEntries,
    humanization: {
      currentPreview,
      tunedDraft,
      comparison,
      tuningRecommendations,
    },
    recentInteractions,
    flaggedFriction,
    suggestedEdits: [
      {
        id: "tone_warm_opening",
        area: "tone",
        title: "Warm the opening without losing control",
        summary: "Add one calm acknowledgment before the next-step sentence.",
        draftChange:
          "Start with a brief acknowledgment like 'Of course, I can help with that' before moving into the next step.",
        rationale:
          "Warmth is currently under target and the opening should feel calmer before canary review.",
        reviewState: "pending",
        sourceLabels: suggestionSourceLabels(primarySourceLabel),
      },
      {
        id: "objection_reduce_repetition",
        area: "objection",
        title: "Reduce objection repetition",
        summary: "Collapse duplicate reassurance and vary repeated sentence starts.",
        draftChange:
          "Keep one acknowledgment, one next-step sentence, and one distinct reassurance sentence instead of repeating the same opener.",
        rationale:
          "The objection layer is tripping current repetition QA and needs cleaner variety.",
        reviewState: "pending",
        sourceLabels: suggestionSourceLabels(primarySourceLabel),
      },
      {
        id: "close_strengthen_next_step",
        area: "close",
        title: "Strengthen the close",
        summary: "End with one clear next step and a courteous close.",
        draftChange:
          "Close with a brief next-step line followed by a polite ending such as 'Happy to help.'",
        rationale:
          "The preview reply needs a clearer close before it should move forward in the draft queue.",
        reviewState: "pending",
        sourceLabels: suggestionSourceLabels(primarySourceLabel),
      },
      {
        id: "blocked_medical_scope",
        area: "prompt",
        title: "Do not expand into medical advice",
        summary:
          "Studio can show the friction, but it cannot turn that request into a clinical prompt change.",
        draftChange:
          "No draft edit is available. Keep the response in front-desk scope and route clinical questions to staff.",
        rationale:
          "Medical advice, diagnosis, and dosage remain outside current VetCan capabilities.",
        reviewState: "blocked",
        blockedReason: "medical_scope_blocked",
        sourceLabels: ["Chat conversation"],
      },
    ],
  };
}
